// coffee-taste.js — turn the onboarding taste answer into real brew guidance.
//
// Onboarding asked "how do you take it?" and stored the answer as
// preferences.coffee_taste_pref. Nothing read it. A setup question that changes
// nothing is worse than no question — it implies the app is listening when it
// isn't. This is what makes it mean something.
//
// The answer seeds preferences.coffee_rules, which is the system prompt the
// morning dial-in coach runs on (see DEFAULT_MORNING_BREW_RULES in ai.js). The
// user can edit it afterwards in Settings; this only supplies the starting
// point, so a later hand-edit is never overwritten.

export const TASTE_PROFILES = {
  smooth: {
    id: "smooth",
    label: "Smooth and sweet",
    hint: "Low acidity, chocolate and caramel, easy on the stomach",
    // Deterministic nudges, applied on top of the profile's base values.
    bias: { ratio: +0.5, tempF: -1, grind: +0.2 },
    rules: `Goal: sweet, milk-chocolate and caramel forward, smooth, low bitterness, very low acidity, clean finish, easy on the stomach.

Core philosophy: MORE COFFEE IS ALMOST NEVER THE FIX. Chase sweetness and flavor with grind, temperature, and bloom — not dose. Bias every recommendation toward smoother, sweeter, lower extraction. Prefer medium roasts and washed coffees; dark and French roasts tend to score poorly (too bitter, hard on the stomach).

When something tastes off, reach for a slightly coarser grind, slightly cooler water, or a bloom tweak before ever increasing the dose.`,
  },

  bright: {
    id: "bright",
    label: "Bright and fruity",
    hint: "Higher acidity, floral and citrus notes, more clarity",
    bias: { ratio: +1, tempF: +2, grind: -0.2 },
    rules: `Goal: clarity and lift — floral, citrus and stone-fruit notes, a lively acidity, a clean and light body.

Core philosophy: protect aromatics. Favor lighter roasts and washed or naturally processed coffees with fruit-forward notes. A slightly finer grind and hotter water bring out clarity; a looser ratio keeps the cup from turning syrupy.

When something tastes flat or dull, go slightly finer or hotter before adding coffee. Treat heaviness and muddiness as the failure modes to avoid.`,
  },

  strong: {
    id: "strong",
    label: "Strong and full-bodied",
    hint: "Heavier body, more concentration, low acidity",
    bias: { ratio: -1, tempF: 0, grind: -0.1 },
    rules: `Goal: a dense, full-bodied cup — heavy mouthfeel, deep cocoa and dried-fruit notes, low acidity, a long finish.

Core philosophy: concentration comes from ratio, not over-extraction. Tighten the ratio to build body, and keep the grind and temperature moderate so the extra strength does not arrive as bitterness.

When something tastes thin, tighten the ratio first. Treat astringency and a drying finish as the signal that extraction has gone too far — back off grind or temperature rather than the dose.`,
  },
};

export const DEFAULT_TASTE = "smooth";

export function tasteProfile(pref) {
  return TASTE_PROFILES[pref] || TASTE_PROFILES[DEFAULT_TASTE];
}

/**
 * Build a complete coffee_rules body for a taste preference.
 *
 * `gearLine` describes the user's actual equipment so the coach does not assume
 * a Fellow Aiden — see lib/coffee-gear.js.
 */
export function coffeeRulesFor(pref, gearLine = "") {
  const p = tasteProfile(pref);
  const gear = gearLine
    ? `They brew on ${gearLine}.`
    : "Ask about their equipment before giving equipment-specific numbers.";
  return `You are the user's coffee dial-in coach. ${gear}

${p.rules}

Always give actual values rather than saying "default". When adjusting, change one variable at a time and say which one and why.`;
}

/**
 * One-line description of the user's gear, for the prompt above.
 * Takes the resolved object from coffeeGear().
 */
export function gearLine(gear) {
  if (!gear?.methodKnown) return "";
  const grinder = gear.grinder?.min == null
    ? "a grinder with no numbered scale"
    : `a ${gear.grinder.label}`;
  return `${gear.method.label.toLowerCase()} with ${grinder}`;
}
